import { getLanguageOptionFromDisplayLanguage } from 'features/campaign/campaign.util';
import { useFlavorDisplayLanguage } from 'features/traffic/campaigns/useFlavor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DirectPropsAltTextWithGenAIFieldProps } from '../types';
import { deriveImageFieldPath, getImageFieldValue } from '../utils';
import { DirectPropsLayout } from './DirectPropsAltTextLayout';

export const DirectPropsAltText = ({
  value = '',
  onChange,
  onBlur,
  formContext,
  fieldId,
  imageFieldPath: explicitImageFieldPath,
  label = 'Alt Text',
  placeholder = 'Enter value...',
  disabled,
  uiSchema,
  error: hasError,
}: DirectPropsAltTextWithGenAIFieldProps) => {
  const imageFieldPath = useMemo(() => {
    if (explicitImageFieldPath) return explicitImageFieldPath;
    if (fieldId) return deriveImageFieldPath(fieldId, uiSchema);
    return '';
  }, [
    explicitImageFieldPath,
    fieldId,
    uiSchema,
  ]);

  const { assetId, imageType, locale } = useMemo(() => {
    const imageFieldValue = getImageFieldValue(formContext, imageFieldPath);

    const extractedAssetId = typeof imageFieldValue === 'string' && imageFieldValue.startsWith('JourneysAssetId-') ? imageFieldValue : null;

    const context = formContext as Record<string, unknown>;
    const derivedLocale = (context?.language as string) || 'en_US';

    return {
      assetId: extractedAssetId,
      imageType: null, // Not available in the context
      locale: derivedLocale,
    };
  }, [
    formContext,
    imageFieldPath,
  ]);

  const displayLanguage = useFlavorDisplayLanguage();
  const defaultLanguageOption = useMemo(
    () => getLanguageOptionFromDisplayLanguage(displayLanguage),
    [
      displayLanguage,
    ],
  );

  const finalLocale = useMemo(() => {
    if (locale) return locale;
    return defaultLanguageOption?.language ?? 'en_US';
  }, [
    locale,
    defaultLanguageOption,
  ]);

  const [suggestion, setSuggestion] = useState<string>('');
  const [errorObj, setErrorObj] = useState<Error | null>(null);
  const [savedDeviceName, setSavedDeviceName] = useState('');

  const isDecorative = disabled || false;
  const disableGenerate = isDecorative;
  const showImageSizeWarning = false;
  const imageValidationMessage = undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Indirect dependency
  useEffect(() => {
    setSuggestion('');
    setErrorObj(null);
  }, [
    assetId,
    finalLocale,
  ]);

  const handleClearError = useCallback(() => {
    setErrorObj(null);
  }, []);

  const handleClearSuggestion = useCallback(() => {
    setSuggestion('');
  }, []);

  const handleUseSuggestion = useCallback(() => {
    if (onChange && suggestion) {
      onChange(suggestion);
    }
    handleClearSuggestion();
  }, [
    onChange,
    suggestion,
    handleClearSuggestion,
  ]);

  const handleAltTextGenerated = useCallback(
    (altText: string) => {
      setSuggestion(altText);
      handleClearError();
    },
    [
      handleClearError,
    ],
  );

  const handleError = useCallback((err: Error) => {
    setErrorObj(err);
  }, []);

  const handleSubmit = useCallback(() => {
    setSuggestion('');
    handleClearError();
  }, [
    handleClearError,
  ]);

  const textareaProps = useMemo(
    () => ({
      'aria-label': 'Alt Text',
      disabled: isDecorative,
      placeholder,
      ...(fieldId && {
        'data-cy': `input-${fieldId}`,
      }),
    }),
    [
      isDecorative,
      placeholder,
      fieldId,
    ],
  );

  const generateButtonProps = useMemo(
    () => ({
      assetId,
      disableGenerate,
      imageType,
      locale: finalLocale,
      onAltTextGenerated: handleAltTextGenerated,
      onError: handleError,
      onNameChange: setSavedDeviceName,
      onSubmit: handleSubmit,
      productName: savedDeviceName,
    }),
    [
      assetId,
      disableGenerate,
      imageType,
      finalLocale,
      handleAltTextGenerated,
      handleError,
      handleSubmit,
      savedDeviceName,
    ],
  );

  return (
    <DirectPropsLayout
      error={errorObj?.message}
      generateButtonProps={generateButtonProps}
      hasValidationError={hasError}
      imageValidationMessage={imageValidationMessage}
      isDecorative={isDecorative}
      label={label}
      onBlur={onBlur}
      onChange={onChange}
      onClearError={handleClearError}
      onUseSuggestion={handleUseSuggestion}
      showImageSizeWarning={showImageSizeWarning}
      suggestion={suggestion}
      textareaProps={textareaProps}
      value={value}
    />
  );
};
